import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAnalytics } from '../analytics/use-analytics';

import { checkApiCookiesEnabled } from './cookie-check-client';

type CookieStatus = 'checking' | 'enabled' | 'blocked';

type CookieCheckContextValue = {
  readonly status: CookieStatus;
  readonly startCookieCheck: () => void;
};

const CookieCheckContext = createContext<CookieCheckContextValue | null>(null);

type CookieCheckProviderProps = {
  readonly apiUrl: string;
  readonly children: ReactNode;
};

const CookieCheckProvider = ({ apiUrl, children }: CookieCheckProviderProps) => {
  const [status, setStatus] = useState<CookieStatus>('checking');
  const checkStarted = useRef(false);
  const analytics = useAnalytics();

  const startCookieCheck = useCallback(() => {
    if (checkStarted.current) return;
    checkStarted.current = true;

    checkApiCookiesEnabled(apiUrl)
      .then((cookiesEnabled) => setStatus(cookiesEnabled ? 'enabled' : 'blocked'))
      .catch(() => setStatus('enabled'));
  }, [apiUrl]);

  useEffect(() => {
    if (status === 'blocked') {
      analytics.trackEvent({ event: 'cookies_blocked' });
    }
  }, [analytics, status]);

  const value = useMemo(() => ({ startCookieCheck, status }), [startCookieCheck, status]);

  return <CookieCheckContext.Provider value={value}>{children}</CookieCheckContext.Provider>;
};

const useCookieCheck = (): CookieCheckContextValue => {
  const context = useContext(CookieCheckContext);
  if (!context) {
    throw new Error('useCookieCheck must be used within a CookieCheckProvider');
  }
  return context;
};

export { CookieCheckProvider, useCookieCheck };
export type { CookieStatus };
